import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { SAME_SITE_STRICT } from '@constants/cookies';
import { IS_DEV_NODE_ENV } from '@constants/nodeEnvs';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import Cookies from 'js-cookie'; // Import js-cookie

const DARK_MODE_COOKIE = 'xceed-dark-mode';
const cookieDomain = IS_DEV_NODE_ENV ? 'localhost' : '.xceed.me';

export const DarkModeContext = createContext({
  isDarkMode: false,
  toggleDarkMode: () => {},
  isActive: false,
});

export const DarkModeProvider = ({ children }: { children: ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { isDarkModeEnabled: isActive } = useContext(FeatureFlagContext);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);

    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    Cookies.set(DARK_MODE_COOKIE, newDarkMode.toString(), {
      domain: cookieDomain,
      path: '/',
      sameSite: SAME_SITE_STRICT,
      secure: true,
      expires: 3650,
    });
  };

  const determineInitialDarkMode = () => {
    const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const darkModeCookie = Cookies.get(DARK_MODE_COOKIE); // Retrieve value from cookies

    if (isActive) {
      if (darkModeCookie === undefined) {
        return prefersDarkMode;
      } else {
        return darkModeCookie === 'true';
      }
    } else {
      return false;
    }
  };

  const initialDarkMode = determineInitialDarkMode();

  useEffect(() => {
    if (initialDarkMode !== isDarkMode) {
      setIsDarkMode(initialDarkMode);
      if (initialDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, [initialDarkMode, isDarkMode]);

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode, isActive }}>{children}</DarkModeContext.Provider>
  );
};
